import { Dispatch, SetStateAction } from "react";
import { Button, Dialog, DialogActions, DialogContent } from "@mui/material";
import { useTranslation } from "react-i18next";
import { MetaData } from "../../../Login/typings/login.types";
import {
  AdvancedBookingSettingsGroup,
  AdvancedBookingSettingsSetters,
  CheckinPolicyGroup
} from "../CompanyCard.types";
import { FileObject } from "../../../FileDropzone/typings/FileObject.type";
import { CompanyCardSettingContent } from "./CompanyCard-Setting-Content.partial";

type CompanyCardSettingDialogProps = {
  open: boolean;
  newValues: MetaData;
  companyMetaData: MetaData;
  companyName: string | null;
  brandColor?: string | null;
  logoUrl: string | null;
  newSetting: AdvancedBookingSettingsGroup;
  setNewSetting: AdvancedBookingSettingsSetters;
  checkinEnabledGrp: CheckinPolicyGroup;
  setCheckinEnabledGrp: Dispatch<SetStateAction<CheckinPolicyGroup>>;
  homeOffice: { homeOfficeDays: [number, number]; enabled: boolean; mandatory: boolean };
  setHomeOffice: Dispatch<
    SetStateAction<{ homeOfficeDays: [number, number]; enabled: boolean; mandatory: boolean }>
  >;
  setNewValues: Dispatch<SetStateAction<MetaData>>;
  onSave: () => void;
  onEditLogo: () => void;
  onClose: () => void;
  logoFile: FileObject[] | undefined;
  onDeleteCompany?: () => void;
};

export function CompanyCardSettingDialog({
  open,
  newValues,
  companyMetaData,
  companyName,
  brandColor,
  logoUrl,
  newSetting,
  setNewSetting,
  checkinEnabledGrp,
  setCheckinEnabledGrp,
  homeOffice,
  setHomeOffice,
  setNewValues,
  onSave,
  onEditLogo,
  onClose,
  logoFile,
  onDeleteCompany
}: Readonly<CompanyCardSettingDialogProps>) {
  const { t } = useTranslation();
  const {
    wpSetting,
    parkPlSetting,
    eChargePlSetting,
    confZnSetting,
    vehicleSetting,
    visitorSetting
  } = newSetting;
  const {
    setWpSetting,
    setParkPlSetting,
    setEChargePlSetting,
    setConfZnSetting,
    setVehicleSetting,
    setVisitorSetting
  } = setNewSetting;

  return (
    <Dialog
      open={open}
      maxWidth="xl"
      fullWidth
      slotProps={{ paper: { sx: { backgroundImage: "none" } } }}
      hideBackdrop
    >
      {/* Content */}
      <DialogContent>
        <CompanyCardSettingContent
          newValues={newValues}
          setNewValues={setNewValues}
          companyMetaData={companyMetaData}
          companyName={companyName}
          brandColor={brandColor}
          logoUrl={logoUrl}
          logoFile={logoFile}
          onEditLogo={onEditLogo}
          newSetting={{
            wpSetting,
            parkPlSetting,
            eChargePlSetting,
            confZnSetting,
            vehicleSetting,
            visitorSetting
          }}
          setNewSetting={{
            setWpSetting,
            setParkPlSetting,
            setEChargePlSetting,
            setConfZnSetting,
            setVehicleSetting,
            setVisitorSetting
          }}
          checkinEnabledGrp={checkinEnabledGrp}
          setCheckinEnabledGrp={setCheckinEnabledGrp}
          homeOffice={homeOffice}
          setHomeOffice={setHomeOffice}
          onDeleteCompany={onDeleteCompany}
        />
      </DialogContent>

      {/* Actions */}
      <DialogActions>
        <Button
          color={"secondary"}
          onClick={onClose}
          data-testid="company-card-setting-dialog-cancel-btn"
        >
          {t("Cancel")}
        </Button>
        <Button onClick={onSave} data-testid="company-card-setting-dialog-save-btn">
          {t("Save")}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
